import { FormGroup, Input } from '@starsoft/common/components';
import {
  CreateKycInputDto,
  FetchUserIdentitiesValidateInputDto,
} from '@starsoft/common/dto';
import { KycIdentityOriginId } from '@starsoft/common/enums';
import { extractFirstAndLastName } from '@starsoft/common/utils/user-identity';
import { useFormContext } from 'react-hook-form';
import { UserIdentityFormFields } from '../../../../../../../../enums/user-identity/form-fields';
import { useTranslation } from '../../../../../../../../hooks';
import { useUserIdentityLookUpHandler } from '../../../../../../../../hooks/user-identity/useUserIdentityLookUpHandler';
import { useUserOriginId } from '../../../../../../../../hooks/user-identity/useUserOriginId';
import { KycUserIdentityFormPersolanIdentityLookUpNameInputsProps } from './props';

/**
 * KYC User Identity Form Personal Identity Look Up Name Inputs Component
 * Renders name input fields for KYC identity verification based on the origin country.
 * Different countries have different name field requirements (e.g. Brazil requires first/last name separately,
 * while others use a single name field).
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isContainerColor - Whether to use container background color for inputs
 * @param {boolean} props.isSkeleton - Whether to show skeleton loading state
 * @param {boolean} props.readonly - Whether the inputs should be read-only
 * @param {string} props.externalOriginId - External origin ID to override the user's origin ID
 * @returns {JSX.Element|null} Name input fields based on origin country
 *
 * @example
 * // For Brazil (separate first/last name fields)
 * <KycUserIdentityFormPersolanIdentityLookUpNameInputs
 *   isContainerColor={true}
 *   isSkeleton={false}
 *   readonly={false}
 *   externalOriginId="BR"
 * />
 *
 * // For other countries (single name field)
 * <KycUserIdentityFormPersolanIdentityLookUpNameInputs
 *   isContainerColor={false}
 *   isSkeleton={true}
 *   readonly={true}
 *   externalOriginId="IN"
 * />
 */
export default function KycUserIdentityFormPersolanIdentityLookUpNameInputs({
  isContainerColor,
  isSkeleton,
  readonly,
  externalOriginId,
}: KycUserIdentityFormPersolanIdentityLookUpNameInputsProps) {
  const { t } = useTranslation('common');
  const originId = useUserOriginId() ?? externalOriginId;
  const methods = useFormContext<
    FetchUserIdentitiesValidateInputDto & CreateKycInputDto
  >();

  const {
    register,
    formState: { dirtyFields },
  } = methods;

  const {
    useUserIdentityValidateByOriginIdQuery: { isLoading, errorFieldsMap, data },
  } = useUserIdentityLookUpHandler({
    methods,
    isSkeleton,
    readonly,
    externalOriginId,
  });

  const { firstName, lastName } = extractFirstAndLastName(data?.name);

  // Render different input fields based on origin country
  switch (originId?.toString()) {
    // Brazil requires separate first/last name fields
    case KycIdentityOriginId.Brazil?.toString():
      return (
        <>
          <FormGroup
            label={t('first_name_label')}
            loading={{
              label: true,
              secondary: !isContainerColor,
            }}
            isLoading={isSkeleton}
          >
            <Input
              placeholder={t('first_name_label')}
              readOnly
              showSpinner={isLoading}
              isContainerColor={isContainerColor}
              value={firstName}
              isLoading={isSkeleton}
            />
          </FormGroup>
          <FormGroup
            label={t('last_name_label')}
            loading={{
              label: true,
              secondary: !isContainerColor,
            }}
            isLoading={isSkeleton}
          >
            <Input
              placeholder={t('last_name_label')}
              readOnly
              showSpinner={isLoading}
              isContainerColor={isContainerColor}
              value={lastName}
              isLoading={isSkeleton}
            />
          </FormGroup>
        </>
      );

    // Other countries use a single name field
    case KycIdentityOriginId.India?.toString():
    case KycIdentityOriginId.Argentina?.toString():
    case KycIdentityOriginId.Mexico?.toString():
    case KycIdentityOriginId.Indonesia?.toString():
    case KycIdentityOriginId.Peru?.toString():
    case KycIdentityOriginId.SouthAfrica?.toString():
    case KycIdentityOriginId.Nigeria?.toString():
    case KycIdentityOriginId.Singapore?.toString():
      return (
        <FormGroup
          label={t('name_label')}
          loading={{
            label: true,
            secondary: !isContainerColor,
          }}
          isLoading={isSkeleton}
        >
          <Input
            placeholder={t('name_label')}
            showSpinner={isLoading}
            register={register}
            name="identity.name"
            isContainerColor={isContainerColor}
            isError={
              errorFieldsMap?.has(UserIdentityFormFields.Name) &&
              dirtyFields?.identity?.name
            }
            errorMessage={errorFieldsMap?.get(UserIdentityFormFields.Name)}
            isLoading={isSkeleton}
            readOnly={readonly}
          />
        </FormGroup>
      );
  }
}
